import { Modal as NextModal, ModalBodyProps } from '@nextui-org/react';
import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheetProps } from 'react-spring-bottom-sheet';

import { inMobile, inTablet, useWindowDimensions } from '../../hooks/useDimensions';
import { StyledLoading } from '../Button';
import { ButtonRole } from '../Button';
import {
  ButtonDiv,
  ContentContainer,
  SecondaryButtonDiv,
  StyledBottomSheet,
  StyledFooter,
  StyledModal,
} from './Modal.styles';
import ModalHeader from './ModalHeader';

export enum BottomSheetChangeBreakpoint {
  Tablet = 'tablet',
  Mobile = 'mobile',
}

export interface ModalProps {
  children: React.ReactNode;
  open: boolean;
  title?: string;
  cancelButtonTitle?: string;
  submitButtonTitle?: string;
  reverseActionsOrder?: boolean;
  width?: string;
  loading?: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  onCancel?: () => void;
  onGoBack?: () => void;
  hideFooter?: boolean;
  centerTitle?: boolean;
  buttonRole?: ButtonRole;
  hideCloseButton?: boolean;
  disabledSubmitButton?: boolean;
  hideTitle?: boolean;
  fullScreen?: boolean;
  hideCancelButton?: boolean;
  backgroundColor?: string;
  noPadding?: boolean;
  /**
   * @description Should receive a valid CSS string.
   * @example "1rem 1.25rem 0 1rem"
   */
  mobileTitleMarginBottom?: string;
  /**
   * @description Should receive a valid CSS string.
   * @example "1rem 1.25rem 0 1rem"
   */
  mobileContainerPadding?: string;
  mobileShowBorderTop?: boolean;
  /**
   * @description Should receive a valid CSS string.
   * @example "1rem"
   */
  desktopSubmitButtonWidth?: string;
  desktopCancelButtonWidth?: string;
  bottomSheetChangeBreakpoint?: BottomSheetChangeBreakpoint;
  /**
   * If true, the inner scroll will be disabled on mobile.
   * This is used for example for the filters bottomsheet, where the scroll is handled by the filters component.
   */
  mobileDisableInnerScroll?: boolean;
  zIndexBottomSheet?: number;
}

const Modal = forwardRef(
  (
    {
      open,
      children,
      title,
      cancelButtonTitle,
      submitButtonTitle,
      reverseActionsOrder,
      width = '32.5rem',
      loading,
      onClose,
      onSubmit,
      onCancel,
      onGoBack,
      hideFooter = false,
      centerTitle = false,
      hideCloseButton = false,
      disabledSubmitButton = false,
      hideTitle,
      fullScreen,
      hideCancelButton,
      backgroundColor,
      noPadding,
      mobileTitleMarginBottom,
      mobileContainerPadding,
      mobileShowBorderTop,
      desktopSubmitButtonWidth,
      desktopCancelButtonWidth,
      bottomSheetChangeBreakpoint,
      mobileDisableInnerScroll,
      buttonRole = ButtonRole.Primary,
      zIndexBottomSheet,
      ...rest
    }: ModalProps & BottomSheetProps & Partial<ModalBodyProps>,
    ref,
  ) => {
    const { t } = useTranslation();
    const { width: windowWidth } = useWindowDimensions();
    const submitButtonRef = React.createRef<any>();

    const handleCancelClick = () => {
      onClose?.();
      onCancel?.();
    };

    const Header = () => (
      <ModalHeader
        centerTitle={centerTitle}
        title={title}
        mobileTitleMarginBottom={mobileTitleMarginBottom}
        hideCloseButton={hideCloseButton}
        onClose={onClose}
        onGoBack={onGoBack}
        bottomSheetChangeBreakpoint={bottomSheetChangeBreakpoint}
      />
    );

    const CancelButton = ({ width }: { width?: string }) => (
      <SecondaryButtonDiv
        $width={width}
        onClick={() => handleCancelClick()}
        $bottomSheetChangeBreakpoint={bottomSheetChangeBreakpoint}
      >
        {cancelButtonTitle || t('common.cancel')}
      </SecondaryButtonDiv>
    );
    const SubmitButton = ({ width }: { width?: string }) => (
      <ButtonDiv
        ref={submitButtonRef}
        disabled={disabledSubmitButton}
        loading={loading}
        onClick={() => !disabledSubmitButton && onSubmit()}
        $width={width}
        $bottomSheetChangeBreakpoint={bottomSheetChangeBreakpoint}
        $buttonRole={buttonRole}
      >
        {loading ? <StyledLoading size="sm" /> : submitButtonTitle || t('common.ok')}
      </ButtonDiv>
    );
    const Footer = ({ $isDesktop }: { $isDesktop?: boolean }) => (
      <StyledFooter
        reverse={reverseActionsOrder}
        $showMobileBorderTop={mobileShowBorderTop}
        $bottomSheetChangeBreakpoint={bottomSheetChangeBreakpoint}
      >
        {!hideCancelButton && <CancelButton width={$isDesktop && desktopCancelButtonWidth} />}
        <SubmitButton width={$isDesktop && desktopSubmitButtonWidth} />
      </StyledFooter>
    );

    const renderCondition =
      bottomSheetChangeBreakpoint === BottomSheetChangeBreakpoint.Tablet
        ? inTablet(windowWidth)
        : inMobile(windowWidth);

    return (
      <>
        {renderCondition ? (
          <StyledBottomSheet
            ref={ref}
            open={open}
            onDismiss={onClose}
            $color={backgroundColor}
            $fullScreen={fullScreen}
            $noPadding={noPadding}
            $bottomSheetChangeBreakpoint={bottomSheetChangeBreakpoint}
            $zIndex={zIndexBottomSheet}
            {...rest}
          >
            {!hideTitle && <Header />}
            <ContentContainer
              id="modal_content"
              $padding={mobileContainerPadding}
              $disableScroll={mobileDisableInnerScroll}
            >
              {children}
            </ContentContainer>
            {!hideFooter && <Footer />}
          </StyledBottomSheet>
        ) : (
          <StyledModal open={open} width={width} onClose={onClose} fullScreen={fullScreen} $color={backgroundColor}>
            {!hideTitle && <Header />}
            <NextModal.Body noPadding={noPadding} {...rest}>
              {children}
            </NextModal.Body>
            {!hideFooter && <Footer $isDesktop />}
          </StyledModal>
        )}
      </>
    );
  },
);

export default Modal;
